import React, { useCallback, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';

import { checkImageFile, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomFormLabel, ErrorMessage, FileErrorMessage, FileInputButton } from 'components';
import { Box, IconButton } from '@mui/material';
import * as icons from 'mdi-material-ui';

const ImageUpload = ({
    selectedFiles,
    removeFile,
    ContainerProps,
    handleFileChange,
    label,
    inline,
    id,
    error,
    width = 'auto',
    acceptedFileTypes,
}) => {
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);

    const handleInterceptChange = useCallback(
        e => {
            const file = e.target?.files[0];

            // validate file size
            if (validateFileSize(file)) {
                setFileSizeError(false);
            } else {
                setFileSizeError(true);
            }

            // validate file type
            checkImageFile(
                file,
                () => {
                    setFileTypeError(false);
                    handleFileChange(e);
                },
                () => setFileTypeError(true)
            );
        },
        [handleFileChange]
    );

    const files = useMemo(
        () =>
            selectedFiles?.length
                ? selectedFiles.filter(f => !!f)
                : !!selectedFiles
                ? [selectedFiles]
                : [],
        [selectedFiles]
    );

    return (
        <Box mt="20px" mb={5} width={width} {...ContainerProps}>
            {label && <CustomFormLabel>{label}</CustomFormLabel>}
            <Box mb={1} display={inline ? 'block' : 'flex'} alignItems="center" width="100%">
                {files.map((file, i) => (
                    <Box
                        key={file.name + i}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={1}
                        flex="0 1 100%"
                    >
                        <Box display="flex" alignItems="center" gap={2}>
                            {file instanceof File && (
                                <Box
                                    component="img"
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    sx={{ width: '100px', height: '100px', objectFit: 'contain' }}
                                />
                            )}
                            {!!file.name && (
                                <IconButton size="small" id={i} onClick={removeFile}>
                                    <icons.Delete />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                ))}
                {inline && (
                    <FileInputButton
                        noMargin
                        acceptedFileTypes={acceptedFileTypes}
                        showIcon
                        label={texts.UPLOAD_FILE_BUTTON}
                        onFileChange={handleInterceptChange}
                        id={id}
                    />
                )}
            </Box>
            {fileSizeError && <FileErrorMessage variant="size" useMargin={false} />}
            {fileTypeError && <FileErrorMessage variant="type" useMargin={false} />}
            <ErrorMessage gutterBottom message={error} />
            {!inline && (
                <FileInputButton
                    acceptedFileTypes={acceptedFileTypes}
                    noMargin
                    showIcon
                    label={texts.UPLOAD_FILE_BUTTON}
                    onFileChange={handleInterceptChange}
                    id={id}
                />
            )}
        </Box>
    );
};

ImageUpload.propTypes = {
    id: PropTypes.string,
    inline: PropTypes.bool,
    selectedFiles: PropTypes.array,
    removeFile: PropTypes.func,
    ContainerProps: PropTypes.object,
    handleFileChange: PropTypes.func,
    label: PropTypes.string,
    error: PropTypes.string,
    width: PropTypes.string,
    acceptedFileTypes: PropTypes.array,
};

export default ImageUpload;
